/**
 * Haptic feedback utility for native-like interactions
 * Uses @capacitor/haptics on native platforms, falls back to Vibration API on web
 */
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

export type HapticStyle = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

const isNative = Capacitor.isNativePlatform();

const impactMap: Record<string, ImpactStyle> = {
  light: ImpactStyle.Light,
  medium: ImpactStyle.Medium,
  heavy: ImpactStyle.Heavy,
};

const notificationMap: Record<string, NotificationType> = {
  success: NotificationType.Success,
  warning: NotificationType.Warning,
  error: NotificationType.Error,
};

// Web fallback patterns (navigator.vibrate doesn't work on iOS, but works on Android browsers)
const vibrationPatterns: Record<HapticStyle, number | number[]> = {
  light: 10,
  medium: 25,
  heavy: 50,
  success: [10, 50, 20],
  warning: [30, 50, 30],
  error: [50, 100, 50],
};

export function triggerHaptic(style: HapticStyle = 'light'): void {
  if (isNative) {
    if (style in impactMap) {
      Haptics.impact({ style: impactMap[style] });
    } else if (style in notificationMap) {
      Haptics.notification({ type: notificationMap[style] });
    }
    return;
  }

  // Web fallback
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    try {
      navigator.vibrate(vibrationPatterns[style]);
    } catch {
      // Silently fail if vibration is not supported
    }
  }
}

export function useHaptics() {
  const light = () => triggerHaptic('light');
  const medium = () => triggerHaptic('medium');
  const heavy = () => triggerHaptic('heavy');
  const success = () => triggerHaptic('success');
  const warning = () => triggerHaptic('warning');
  const error = () => triggerHaptic('error');

  return { light, medium, heavy, success, warning, error, trigger: triggerHaptic };
}
